import { useEffect, useState, useRef } from 'react';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { productionBatchesSearch } from '@reducers/search';
import { ProductionBatch } from '@interfaces/search';
import { Events, TimelineDataWithLoadingState } from '@interfaces/timeline';
import { TIMELINE_BATCH_SELECT } from '@components/machines/components/timeline/constants';

interface MachineData {
  machineId: number;
  events: Events;
  productionBatchesAndCounts: TimelineDataWithLoadingState<{ batches: ProductionBatch[]; counts: any[] }>;
}

interface EnhancedMachineData extends MachineData {
  enhancedBatchesData: TimelineDataWithLoadingState<ProductionBatch[]>;
}

interface UseMultiMachineEnhancedBatchesReturn {
  enhancedMachineData: Record<number, EnhancedMachineData>;
}

export const useMultiMachineEnhancedBatches = (machineTimelineData: Record<number, MachineData>): UseMultiMachineEnhancedBatchesReturn => {
  const dispatch = useAppDispatch();
  const [enhancedBatches, setEnhancedBatches] = useState<Record<number, ProductionBatch[]>>({});
  const abortControllerRef = useRef<AbortController | null>(null);

  // Create hash for dependencies to avoid object reference issues
  const machineDataHash = Object.keys(machineTimelineData)
    .slice(0, 10)
    .map((machineId) => {
      const data = machineTimelineData[Number(machineId)];
      if (!data) return '';

      const batchesHash =
        data.productionBatchesAndCounts?.data?.batches
          ?.slice(0, 10)
          ?.map((batch) => batch.productionBatchId)
          .join(',') || '';
      const eventsHash =
        data.events?.groupedByBatch
          ?.slice(0, 10)
          ?.map((event) => event.productionBatchId)
          .join(',') || '';

      return `${machineId}:${batchesHash}:${eventsHash}`;
    })
    .join('|');

  useEffect(() => {
    (async () => {
      // Abort any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const machineIds = Object.keys(machineTimelineData).map(Number);
      if (machineIds.length === 0) {
        return;
      }

      // Check if all machines have loaded data before proceeding
      const allMachinesLoaded = machineIds.every((machineId) => {
        const data = machineTimelineData[machineId];
        return data?.events?.loaded && data?.productionBatchesAndCounts?.loaded;
      });

      if (!allMachinesLoaded) {
        return;
      }

      // Collect all missing batch IDs across all machines
      const allMissingBatchIds = new Set<number>();
      const machineBaseBatches: Record<number, ProductionBatch[]> = {};

      // Process each machine to find missing batches
      for (const machineId of machineIds) {
        const data = machineTimelineData[machineId];
        if (!data) continue;

        const baseBatches = data.productionBatchesAndCounts?.data?.batches || [];
        machineBaseBatches[machineId] = baseBatches;

        if (!data.events.groupedByBatch?.length || !baseBatches) {
          setEnhancedBatches((prev) => ({ ...prev, [machineId]: baseBatches }));
          continue;
        }

        // Get all productionBatchIds from events for this machine
        const eventBatchIds = new Set<number>();
        data.events.groupedByBatch.forEach((event) => {
          if (event.productionBatchId) {
            eventBatchIds.add(event.productionBatchId);
          }
        });

        // Get existing productionBatchIds from batches for this machine
        const existingBatchIds = new Set(baseBatches.map((batch) => batch.productionBatchId));

        // Find missing productionBatchIds for this machine
        const missingBatchIds = Array.from(eventBatchIds).filter((id) => !existingBatchIds.has(id));

        // Add to global missing batch IDs set
        missingBatchIds.forEach((id) => allMissingBatchIds.add(id));

        if (missingBatchIds.length === 0) {
          setEnhancedBatches((prev) => ({ ...prev, [machineId]: baseBatches }));
        }
      }

      // If no missing batches across all machines, we're done
      if (allMissingBatchIds.size === 0) {
        return;
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        // Create OData filter for all missing batch IDs
        const oDataFilter = Array.from(allMissingBatchIds)
          .map((id) => `ProductionBatchId eq ${id}`)
          .join(' or ');

        const response = await dispatch(
          productionBatchesSearch({
            data: { select: TIMELINE_BATCH_SELECT },
            filter: { oDataFilter },
            select: undefined,
            resetHistory: true,
            signal: abortController.signal,
            ignoreHistory: true,
          })
        );

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        const missingBatches = response?.searchResult || [];

        // Group missing batches by machine ID
        const missingBatchesByMachine: Record<number, ProductionBatch[]> = {};
        missingBatches.forEach((batch: ProductionBatch) => {
          const machineId = batch.machineId;
          if (!missingBatchesByMachine[machineId]) {
            missingBatchesByMachine[machineId] = [];
          }
          missingBatchesByMachine[machineId].push(batch);
        });

        // Combine existing batches with fetched missing batches for each machine
        setEnhancedBatches((prev) => {
          const newState = { ...prev };
          for (const machineId of machineIds) {
            const baseBatches = machineBaseBatches[machineId] || [];
            const additionalBatches = missingBatchesByMachine[machineId] || [];
            newState[machineId] = [...baseBatches, ...additionalBatches];
          }
          return newState;
        });
      } catch (error) {
        const e = error as Error;
        // Don't log error if request was aborted
        if (e.name !== 'AbortError') {
          console.error('Failed to fetch missing batches for multi-machine:', error);
        }
        // Set enhanced batches to base batches on error
        setEnhancedBatches((prev) => {
          const newState = { ...prev };
          for (const machineId of machineIds) {
            newState[machineId] = machineBaseBatches[machineId] || [];
          }
          return newState;
        });
      }
    })();

    // Cleanup function to abort pending requests on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [machineDataHash]);

  // Build the enhanced machine data
  const enhancedMachineData: Record<number, EnhancedMachineData> = {};

  Object.keys(machineTimelineData).forEach((machineIdStr) => {
    const machineId = Number(machineIdStr);
    const data = machineTimelineData[machineId];
    if (data) {
      const enhancedBatchesForMachine = enhancedBatches[machineId] || data.productionBatchesAndCounts?.data?.batches || [];

      enhancedMachineData[machineId] = {
        ...data,
        enhancedBatchesData: {
          loaded: data.productionBatchesAndCounts.loaded,
          data: enhancedBatchesForMachine,
        },
      };
    }
  });

  return {
    enhancedMachineData,
  };
};
